import React, { useState } from "react";
import {
  Edit3,
  Trash2,
  MessageSquare,
  BarChart3,
  AlertCircle,
  Calendar,
} from "lucide-react";
import { Card, CardHeader, CardContent, CardFooter } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ConversationSummary } from "../../types/conversation";

interface ConversationCardProps {
  conversation: ConversationSummary;
  onSelect?: (conversationId: string) => void;
  onEdit?: (conversationId: string, newTitle: string) => void;
  onDelete?: (conversationId: string) => void;
  isSelected?: boolean;
}

export const ConversationCard: React.FC<ConversationCardProps> = ({
  conversation,
  onSelect,
  onEdit,
  onDelete,
  isSelected = false,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(conversation.title);

  const handleTitleEdit = () => {
    if (isEditing && editTitle !== conversation.title && onEdit) {
      onEdit(conversation.conversationId, editTitle);
    }
    setIsEditing(!isEditing);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleTitleEdit();
    } else if (e.key === "Escape") {
      setEditTitle(conversation.title);
      setIsEditing(false);
    }
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffInMs = now.getTime() - new Date(date).getTime();
    const diffInHours = diffInMs / (1000 * 60 * 60);
    const diffInDays = diffInHours / 24;

    if (diffInHours < 1) {
      return "Just now";
    } else if (diffInHours < 24) {
      return `${Math.floor(diffInHours)}h ago`;
    } else if (diffInDays < 7) {
      return `${Math.floor(diffInDays)}d ago`;
    } else {
      return new Date(date).toLocaleDateString();
    }
  };

  const truncateText = (text: string, maxLength: number) => {
    if (!text || text.length <= maxLength) return text;
    return text.substring(0, maxLength) + "...";
  };

  return (
    <Card
      className={`cursor-pointer transition-all duration-200 hover:shadow-md ${
        isSelected ? "ring-2 ring-primary" : ""
      }`}
      onClick={() => onSelect && onSelect(conversation.conversationId)}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1 min-w-0">
            {isEditing ? (
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                onKeyDown={handleKeyDown}
                onBlur={handleTitleEdit}
                className="text-lg font-semibold bg-transparent border-b border-border focus:outline-none focus:border-primary w-full"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            ) : (
              <h3 className="text-lg font-semibold leading-tight truncate">
                {conversation.title || "Untitled Conversation"}
              </h3>
            )}
            <p className="text-sm text-muted-foreground mt-1 leading-relaxed">
              {truncateText(conversation.firstQuery, 120)}
            </p>
          </div>
          <div className="flex items-center space-x-1 ml-2">
            {onEdit && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={(e) => {
                  e.stopPropagation();
                  handleTitleEdit();
                }}
              >
                <Edit3 className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-destructive hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(conversation.conversationId);
                }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="py-0">
        <div className="flex items-center gap-3 text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Calendar className="h-4 w-4" />
            <span>{formatDate(conversation.lastActivity)}</span>
          </div>
          <div className="flex items-center gap-1">
            <MessageSquare className="h-4 w-4" />
            <span>{conversation.messageCount} messages</span>
          </div>
        </div>
      </CardContent>

      <CardFooter className="pt-3">
        <div className="flex items-center gap-2">
          {conversation.hasCharts && (
            <Badge variant="secondary" className="text-xs">
              <BarChart3 className="h-3 w-3 mr-1" />
              Charts
            </Badge>
          )}
          {conversation.hasErrors && (
            <Badge variant="destructive" className="text-xs">
              <AlertCircle className="h-3 w-3 mr-1" />
              Errors
            </Badge>
          )}
          {!conversation.hasCharts && !conversation.hasErrors && (
            <Badge variant="outline" className="text-xs">
              Text Only
            </Badge>
          )}
        </div>
      </CardFooter>
    </Card>
  );
};
